/**
 * WEEKLY NEWSLETTER TEMPLATE
 *
 * To publish a new issue:
 * 1. Paste this week's copy into CURRENT_NEWSLETTER below (keep the same keys).
 * 2. Update publishedAt (YYYY-MM-DD) and edition each week for SEO freshness.
 * 3. Drop image URLs into comic.image / magic.image / story.image / footerImage.
 * 4. Tell the agent "update the newsletter with this copy" and paste the email/JSON,
 *    or edit this file directly.
 * 5. After deploy, prerender snapshot in scripts/prerender-seo-pages.mjs should mirror
 *    this week's H2s so crawlers see real content (not just the SPA shell).
 *
 * Leave blank strings ("") for any optional block you want to hide later.
 * `/newsletter` reads CURRENT_NEWSLETTER; `/newsletter/:slug` reads NEWSLETTER_ARCHIVE.
 */

export type NewsletterIssue = {
  /** URL-safe id, e.g. "2026-09-01" */
  slug: string;
  /** ISO date for this edition, e.g. "2026-09-24" (SEO dateModified) */
  publishedAt: string;
  /** Browser / SEO title stub */
  title: string;

  // Hero
  tagline: string;
  /** e.g. "9/24 · Edition 4" */
  edition: string;
  heroLabel: string;
  heroTitle: string;
  heroSummary: string;

  // Note from David (light panel)
  noteLabel: string;
  noteBody: string;

  // Comic to watch
  comic: {
    label: string;
    title: string;
    image: string;
    imageAlt: string;
    caption: string;
    body: string;
    ctaLabel: string;
    ctaHref: string;
  };

  // Meanwhile at UO (light panel)
  updates: {
    label: string;
    title: string;
    /** Optional. Leave "" to hide. */
    image?: string;
    imageAlt?: string;
    body: string;
  };

  // Magic section
  magic: {
    label: string;
    title: string;
    image: string;
    imageAlt: string;
    /** Small photo credit under the image. Leave "" to hide. */
    imageCredit?: string;
    body: string;
  };

  // Blast from the past / story
  story: {
    label: string;
    title: string;
    image: string;
    imageAlt: string;
    body: string;
  };

  // Dream big
  dream: {
    label: string;
    title: string;
    intro: string;
    body: string;
    contribute: string;
  };

  // Close
  signoff: string;
  /** Optional closing image. Leave "" to hide. */
  footerImage: string;
  footerImageAlt: string;
  footerCaption: string;
};

/** Paste / replace this object each week. */
export const CURRENT_NEWSLETTER: NewsletterIssue = {
  slug: "2026-10-29",
  publishedAt: "2026-10-29",
  title: "The Underground",

  tagline: "A weekly note from behind the scenes",
  edition: "10/29 · Edition 9",
  heroLabel: "All the shit that happens before the show and sometimes during",
  heroTitle: "The Underground",
  heroSummary:
    "One Comic To Watch · New Initiatives · Magic · This Actually Happened",

  noteLabel: "A note from David",
  noteBody: `Hey,

Welcome back to The Underground. This week: Julio Diaz, two more iconic NYC food spots, booking magicians, and the mime at Paragon.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

  comic: {
    label: "One comic to watch",
    title: "10/29 · Julio Diaz",
    image:
      "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-29%20at%204.36.26%E2%80%AFPM.png",
    imageAlt: "Julio Diaz",
    caption: "Julio Diaz on stage.",
    body: `Consistency is key. Julio has been grinding and steadily gets better every time I see him. Julio is scrappy, a great writer, and very patient. A lot of comics get down on themselves when they don't see immediate success, but Julio has a vision for his success that can't be moved. Now that he got passed at the Cellar and is getting spots there, I'm curious to see if he will have time to keep writing new material at the same rate. Great dude who you want to root for. His manager also went to PS 6, or has kids who went to PS 6, so I have even more love for Julio because of that.`,
    ctaLabel: "Watch Julio",
    ctaHref: "https://www.instagram.com/reel/DbNmMI_ugz5/",
  },

  updates: {
    label: "New initiatives",
    title: "Pancakes, froyo, and a Katz's record",
    image: "",
    imageAlt: "",
    body: `Big week for locking in 2 more iconic NYC food spots that I think will generate a ton of buzz. I can't reveal the exact names, but one of them is known for their pancakes and the other is known for their froyo. It will be an eventful December.

Katz's presale went on sale and we had 3,940 people on the link trying to get tickets. Since we started recording this metric, that is the new record.`,
  },

  magic: {
    label: "Magic",
    title: "Booking the magicians",
    image: "",
    imageAlt: "",
    body: `We have only been doing Magic for a year, and something interesting is starting to happen. Brands, individuals, and clubs have been reaching out to see if we can help book a magician for their event. I don't know if we're acting as an agent or a manager. All the same shit to me. But I've been very open with the magicians: if we coordinate these bookings, can we take a 10 percent commission. I don't know how much revenue this will lead to, but as mentioned in the past, the biggest competitor to our magic business is not another show. It's that anyone can hire a magician to stroll around their party and they will have a great time. By no means are we abandoning our magic events, but we are not going to shy away from booking magicians for major firms. It feels very good to deliver a high paying gig to talent.`,
  },

  story: {
    label: "This actually happened",
    title: "The mime at Paragon",
    image:
      "https://storage.googleapis.com/cuo-show-assets/Screenshot%202026-10-29%20at%204.39.20%E2%80%AFPM.png",
    imageAlt: "The line outside Paragon Sports",
    body: `The first year we produced the Paragon Sports festival, which was 2024, for some reason we had the great idea that we wanted to hire a mime. You know, someone who wears face paint and acts like they are trapped behind an invisible wall. When I have my eyes set on something, I pursue it. So through a friend, I got connected to a performer at House of Yes. I won't mention the individual's name because he did a horrific job. I told him I wanted to hire a mime for Paragon Sports to perform while people were waiting in line. What showed up that day wasn't a mime. It was a dude with mascara, holding a baseball bat, blowing sage on everyone. I kindly had to ask this gentleman wtf he was doing, and he told me his specialty was "performance emphasizing time and place." I asked him to leave, apologized to the folks in line, and while I was heated in the moment, this was one of the most absurd things to happen at our shows.`,
  },

  dream: {
    label: "",
    title: "",
    intro: "",
    body: "",
    contribute: "",
  },

  signoff: `Join our waitlist to come to our next big show!

Spread Love,
David`,
  footerImage: "",
  footerImageAlt: "",
  footerCaption: "",
};

/** Past issues for /newsletter/[slug] archives. Newest first after CURRENT. */
export const NEWSLETTER_ARCHIVE: NewsletterIssue[] = [
  {
    slug: "2026-10-22",
    publishedAt: "2026-10-22",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "10/22 · Edition 8",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back to The Underground. This week: Mark Gagnon, Katz's year 5, Underground Trixx, and the year our office was Barnes & Noble.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

    comic: {
      label: "One comic to watch",
      title: "10/22 · Mark Gagnon",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-22%20at%204.21.16%E2%80%AFPM.png",
      imageAlt: "Mark Gagnon",
      caption:
        "Prosecutor Mark Gagnon played a Jewish lawyer in the first Comedy Court pilot with Morgan & Morgan.",
      body: `I'm talking about prosecutor Mark Gagnon. We had the opportunity to film the first pilot episode of Comedy Court with Morgan & Morgan where Mark played a Jewish lawyer. He took this role seriously and was hysterical throughout the live show. Mark has really built himself up both as a standup as well as a dynamic podcaster. Between appearing on Flagrant and Andrew Schulz, he also has 3 of his own channels that do incredibly well.`,
      ctaLabel: "Stay tuned for Comedy Court on YouTube",
      ctaHref: "",
    },

    updates: {
      label: "New initiatives",
      title: "Katz's Deli, year 5",
      image: "",
      imageAlt: "",
      body: `Katz's Deli year 5 is underway. We are doing two ticket drops October 25th and November 3rd. In terms of excitement, this is our biggest event of the year! Currently working on securing some headliners but this industry is ruthless. A lot of the craziest pop-ins we have ever gotten have come together 3 hours before a show. It's not the way I want to live my life, but praying for the best.`,
    },

    magic: {
      label: "Magic",
      title: "Underground Trixx",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-22%20at%204.22.32%E2%80%AFPM.png",
      imageAlt: "Underground Trixx on the train",
      imageCredit: "Frank Franklin from AP",
      body: `We attempted to film the first episode of Underground Trixx, Tuesday, October 20th. To put it short, it was a disaster, but I still fully believe in the concept. Have fantastic magicians perform tricks for crazy New Yorkers on a crowded train. We attempted to be at Penn Station after the Knicks to catch some of the (drunk) fans. The problem was LeBron James took the life out of this city. The energy was dead, and what I realized is that we need to plant at least 6-8 people on the train first to get the energy up before approaching strangers. We need to be the source of energy to draw strangers in rather than going up cold to people with a camera.`,
    },

    story: {
      label: "This actually happened",
      title: "Our office was Barnes & Noble",
      image: "",
      imageAlt: "",
      body: `For the first 3 years of the business, our office was the city of NY or Ethan's apartment because all we would do is walk around to try and find venues for our future shows. However, as my responsibilities grew and I hired folks to the team, I needed to find an office space where we could all meet and work. I landed on the 3rd floor of Barnes & Noble in Union Square. For about a year, we would meet Monday - Friday at Barnes. I did not know this before, but Barnes & Noble is a bit of a loony bin. Homeless folks seek shelter in their bathrooms, and there were a few times when we would be at an important Zoom meeting, and some individual with a substance abuse issue would poke their head into the background behind us. Don't mean to blow his spot, but one of the few highlights of Barnes & Noble is that Chris Rock was a frequent customer; however, I never went up to him.`,
    },

    dream: {
      label: "",
      title: "",
      intro: "",
      body: "",
      contribute: "",
    },

    signoff: `Join our waitlist to come to our next big show!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-10-15",
    publishedAt: "2026-10-15",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "10/15 · Edition 7",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back to The Underground. This week: Andrew Fisher, Comedy Court Phase 2, Underground Trixx, and a lawyer who took a work trip to Tulum.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

    comic: {
      label: "One comic to watch",
      title: "10/15 · Andrew Fisher",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-15%20at%201.51.12%E2%80%AFPM.png",
      imageAlt: "Andrew Fisher",
      caption:
        "Andrew is deaf and performs with friend Phil, who interprets from the crowd while Andrew performs sign language on stage.",
      body: `The first time I saw Andrew, I was mesmerized. Andrew is deaf and performs with friend Phil, who interprets from the crowd while Andrew performs sign language on stage. Together as a duo, they deserve more attention, and I feel if they could get an opportunity on a show like America's Got Talent, their world would change.`,
      ctaLabel: "Watch Andrew",
      ctaHref: "https://www.instagram.com/p/DZD9HAJxufo/",
    },

    updates: {
      label: "New initiatives",
      title: "Comedy Court, Phase 2",
      image:
        "https://storage.googleapis.com/cuo-website/10.13.2026%20-%20Comedy%20Court%20-%20Portraits%2095.JPEG",
      imageAlt: "Comedy Court",
      body: `We know how to execute live events! We have proven this over the last 5 years. We are now working on some incredible digital projects that tie to our live productions. Our first taping of Comedy Court with Morgan & Morgan was one of the proudest moments of my career. I usually get too anxious to laugh during our events, but for this event I was crying. Phase 1 is complete (great live event). Phase 2 of executing a succinct rollout across our digital channel is what I believe could take our business to the next level.`,
    },

    magic: {
      label: "Magic",
      title: "Underground Trixx!",
      image: "",
      imageAlt: "",
      body: `Next week we are starting production for our new digital magic show. Creating a low-budget digital show is something all brands are searching for, and we think we are sitting on a goldmine. We want to help people discover their new favorite magician. We will coordinate our taping days to overlap with major NYC sporting events.`,
    },

    story: {
      label: "This actually happened",
      title: "Our lawyer had a work trip in Tulum",
      image: "",
      imageAlt: "",
      body: `All because someone goes to law school, it does not mean they are a professional. As our business expanded, we needed some legal advice, so we got connected with this lady (I will be respectful and not share her name) who was based out of LA. (First red flag). Every question we asked, she needed to circle back; we had typos in major contracts that a dyslexic like me was able to catch. However, the reason we decided to move on is that she would not respond for weeks because she had a "work trip" in TULUM. If you have a Zoom call with your lawyer, the camera should be ON. Camera off with a lot of background noise because she is on a work trip in TULUM broke the camel's back. I can't confirm if she had a drinking problem, but something wasn't kosher. Not sure what's going on in Mexico, but I don't think work was her priority.`,
    },

    dream: {
      label: "",
      title: "",
      intro: "",
      body: "",
      contribute: "",
    },

    signoff: `Join our waitlist to come to our next big show!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-10-08",
    publishedAt: "2026-10-08",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "10/8 · Edition 6",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened · Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back to The Underground. This week: Elijah Nevels, Comedy Court with Morgan & Morgan, a digital push for magic, a business lunch that turned into a funeral, and a merch ask for NYC streetwear brands.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

    comic: {
      label: "One comic to watch",
      title: "10/8 · Elijah Nevels",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-08%20at%201.54.37%E2%80%AFPM.png",
      imageAlt: "Elijah Nevels",
      caption:
        "Only 22, clean material, and a big smile as soon as he steps on stage. You begin to root for him right away.",
      body: `Elijah was another young star I saw in Montreal while I was there for JFL. Elijah is based out of Cleveland, but made a trip to NY in September and we were lucky to have him do two of our events. I wasn't sure if Montreal was a fluke, but he killed at our shows as well. Some comics are funny, and some have star qualities. In 5 years I'm betting Elijah is a star.`,
      ctaLabel: "Watch Elijah",
      ctaHref: "https://www.instagram.com/p/DcUZBGJpqDG/?hl=en",
    },

    updates: {
      label: "New initiatives",
      title: "Comedy Court!!",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-08%20at%202.01.08%E2%80%AFPM.png",
      imageAlt: "Comedy Court",
      body: `This is our first stab at creating an original IP. Tuesday, October 13th, we are filming a comedy show in a courthouse. The premise is that 5 "killer comedians" will stand trial by performing in front of a jury and a packed courthouse. After each comedian performs a 5-minute set on a topic, they will stand trial and be cross-examined by a prosecutor (a comedian) as well as a judge (another comedian). We've always had the idea of producing a comedy show in a courtroom, and somehow we were able to pitch this concept to Morgan & Morgan, and they encouraged us to build out this idea. As we expand into creating IP, one thing that I think sets ComedyUO apart is how we can tie the digital to an incredible live event. So while Comedy Court is being filmed, I'm very confident that for all the attendees, they will experience a unique hilarious show as well.`,
    },

    magic: {
      label: "Magic",
      title: "Magic is about to get a digital push",
      image: "",
      imageAlt: "",
      body: `Not only do I believe Magic will have its moments with live events, but I think we are about to see a big digital push as well. We are no digital experts even though we are a Gen Z company; however, over the past month I've been actively setting up coffee meetings with young, talented magicians. These magicians are so talented, and I believe some of them, with the right production behind them, can help create the next hit digital show. Hopefully it's us!`,
    },

    story: {
      label: "This actually happened",
      title: "Our business lunch turned into a funeral",
      image: "",
      imageAlt: "",
      body: `Ethan and I were meeting with a business executive about our business and our expansion plans, so we told him to meet us at Dim Sum Palace for lunch, a venue where we frequently produce shows. We arrived at Dim Sum Palace and it was unusually packed. The three of us are doing small talk, and finally when we started to talk numbers, a full band started to play and everyone stood up. Dim Sum Palace was hosting a memorial service and for some reason they were open to the public as well. The band is so loud and everyone is standing so we felt pressure to stand and pay our respects. A casket got taken out of the room. I was very nervous for this meeting and as soon as we started chatting business, we were in the middle of a funeral. Crazy place to hold a service, but it definitely broke the ice and eased any nerves during that meeting. Our negotiations were put on pause for about 45 minutes.`,
    },

    dream: {
      label: "Dreaming big... please help",
      title: "Merchandise! Partnering with NYC streetwear.",
      intro: `We have done some select pieces over the years whenever we have done shows at Katz's Deli, Peter Lugers etc. I think there is a greater opportunity for merchandise and think it would be incredible if we partnered with an NYC streetwear brand to help highlight some of the tentpole events we produce in the city.`,
      body: `Can you imagine if we had some evergreen merch with "Only NY". If you or a loved one works at a cool streetwear brand, please keep us in mind.`,
      contribute:
        "Email david@comedyuo.com if you or someone you know works at a cool NYC streetwear brand!",
    },

    signoff: `Join our waitlist to come to our next big show!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-10-01",
    publishedAt: "2026-10-01",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "10/1 · Edition 5",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened · Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back to The Underground. This week: Daniel Geneen, tentpole season and early access ideas, pairing magic with high-end dining, the night Paragon Sports saved the business, and a big ask on department stores.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

    comic: {
      label: "One comic to watch",
      title: "10/1 · Daniel Geneen",
      image: "https://storage.googleapis.com/cuo-website/Dan%20Geneen%20Acceptable%20Photo.jpg",
      imageAlt: "Daniel Geneen",
      caption:
        "He's on a hot streak right now, but he's quite a yenta. He recently got an applause break at a private event we produced at a membership club, and it was awesome to see him get acknowledged for crushing.",
      body: `Daniel Geneen is becoming a good friend, so this take may be biased. He currently has a bit comparing making love to a game of battleship. Purely incredible, and when the clip drops, it will put up numbers.`,
      ctaLabel: "Watch Daniel",
      ctaHref: "https://www.youtube.com/watch?v=_7tKWpv35LQ",
    },

    updates: {
      label: "New initiatives",
      title: "We are entering tentpole season",
      body: `What I mean by that is between now and the next 6 months we have monthly events that usually draw a ton of organic buzz and sell out instantly. Zabars, Lugers, Katz's, Economy Candy etc.

When these tickets go live, we usually have a few thousand people trying to get tickets for a couple hundred seats. Trying to experiment with ways to provide value to all the people who visit our page. Does it mean creating a subscription service to give our most loyal customers earlier access? Maybe they get free merch - thinking about what some value adds are that we can give to some of our customers who are willing to pay a premium to get earlier access to our tentpole events.`,
    },

    magic: {
      label: "Magic",
      title: "High-end dining meets magic",
      image: "",
      imageAlt: "",
      body: `I believe there is a world where we can pair high-end dining experiences with magic. We recently got access to a loft space in Union Square that has a kitchen. I'm contemplating hiring high-end chefs or working with popular restaurants to come cater the event + still curating the high-level magic shows we have already begun producing.`,
    },

    story: {
      label: "This actually happened",
      title: "Paragon Sports saved our business",
      image:
        "https://storage.googleapis.com/cuo-website/Screenshot%202026-09-30%20at%2012.36.04%E2%80%AFPM.jpg",
      imageAlt: "Zach Blank at Paragon Sports",
      body: `In 2024, Paragon Sports saved our business. Zach Blank is a legend for this one. 2 blocks away from Paragon, we used to host shows at this incredible gym called Presher Fitness (RIP). We were producing 3 shows in one night, approx 450 tickets. We had done multiple events at Presher prior to this date, but around 3 pm when we began loading into the venue, the super of the building said we were not able to enter the premises. We were told that the gym was hosting us illegally and a lot of BS. Long story short, we did not have enough money in the bank account to refund all those tickets. In desperate measure, I called Zach and said: what are the odds we can host 3 shows in approx 3 hours at your store? Paragon was also the only other venue that hosts that quantity of people. He graciously said yes, and within 3 hours we packed all our equipment and put on 3 successful events. It was a miracle we were able to pull it off, because if he didn't say yes, I'm not sure ComedyUO would still be around today.`,
    },

    dream: {
      label: "Dreaming big... please help",
      title: "Department stores! The Annual Macy's Day comedy event.",
      intro: `I have mentioned this before, but I want to be intentional with the dreams so that I can stay focused. They already have flagship NYC events, Thanksgiving & July 4th. I think Jan - March would be electric and completely change the game.`,
      body: `However, Macy's is a large establishment. The young NY crowd we draw would help them tremendously. All that to be said, if you or a loved one has a contact at Macy's or any major department store in NYC, please contact me. Major gift cards and free tickets will be awarded to anyone who can help make this happen.`,
      contribute:
        "Email david@comedyuo.com if you have a contact at Macy's or any major department store in NYC!",
    },

    signoff: `With that, I'll see you next week!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-09-24",
    publishedAt: "2026-09-24",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "9/24 · Edition 4",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened · Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back to The Underground. This week: one comic to watch, a famous deli with a challenge for us, Magic UO heating up, the origin of our silhouette logo, and a personal ask.

As always, email david@comedyuo.com if you have thoughts, tips, or connections. I read them.`,

    comic: {
      label: "One comic to watch",
      title: "9/24 · Brendan Sagalow",
      image: "https://storage.googleapis.com/cuo-website/Sagalow%20Defined.jpeg",
      imageAlt: "Brendan Sagalow",
      caption:
        "I have trouble knowing if Brendan is constantly angry or being sarcastic, but he has a rare ability to come across annoyed on stage while also being hysterical.",
      body: `I think even among the comedy community, Brendan is seen as one of the most underrated comics in the city. My mom once confronted Brendan about his politics off stage, and he handled it very well. For that reason, he has a special place in my heart.

Brendan has a bit about getting into a fight with your girlfriend before attending an event that had me in tears. He is truly one of the best in the game.`,
      ctaLabel: "Watch Brendan",
      ctaHref: "https://www.youtube.com/watch?v=mVV4sfV_nN0",
    },

    updates: {
      label: "New initiatives",
      title: "A famous deli wants a theme",
      body: `I won't say which deli, but you can infer. Recently, a very famous deli reopened in NYC. They are interested in working together, but they tasked us with coming up with a special theme to differentiate this event from our other iconic venues.

I always love when venues push us the extra step to see how we can one-up ourselves. Right now we are brainstorming ideas around matzah balls and other holidays.`,
    },

    magic: {
      label: "Magic",
      title: "1,000 new magic signups. Now we deliver.",
      image: "https://storage.googleapis.com/cuo-website/Fire%20Guy%20Newsletter%20.jpg",
      imageAlt: "Magic UO fire performer",
      body: `1,000 new signups have entered our mailing list expressing interest in magic! Now we need to deliver.

Planning on returning to the Penthouse apartment and fully decking it out by putting some $$ into venue design. We lose access to this penthouse 11/1, so planning on maxing it out until RIP.`,
    },

    story: {
      label: "This actually happened",
      title: "The silhouette logo + \"Gangsta's don't get arrested\"",
      image: "https://storage.googleapis.com/cuo-website/The%20Logo%20Defined.jpg",
      imageAlt: "Daniel's Leather, origin of the ComedyUO silhouette logo",
      body: `December 2021: we did an event at Daniel's Leather on Orchard St. If you are not familiar with this venue, they sell crazy fur and leather jackets to folks primarily in the hip hop community. They have cameras in the shop, but their primary form of security is a Rottweiler.

When setting up for the event, we tried on two of these fancy leather coats and took a picture back to back. What's cool about that photo is the red coat I was wearing was purchased by CeeLo Green the next day. This photo ended up becoming our primary logo for the next 5 years.

At this same event, the head of security from the venue had a bit too much to drink at the end of the night. We, being responsible, told him it was not smart for him to drive home and that we would get him an Uber. He then told us the famous line that we will never forget: "Gangsta's don't get arrested."`,
    },

    dream: {
      label: "Dreaming big... please help",
      title: "I'm getting married today!",
      intro: `This message has been written on Tuesday, as my lady would kill me if I wrote this on the big day. This week's request is a bit different.`,
      body: `How do I balance running a small rocket ship of a business and keep the lady happy? Obviously, this is a tale as old as time for anyone running a company, but sometimes it's good to hear tips and get advice.

Work-life balance is something I already struggle with. I'm very excited for this next chapter of my life, and I already attribute a lot of the success we have had to Isabel keeping me sane.

Much love!`,
      contribute: "Email david@comedyuo.com if you have tips, advice, or anything to contribute!",
    },

    signoff: `With that, I'll see you next week!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-09-17",
    publishedAt: "2026-09-17",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "9/17 · Edition 3",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened · Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back. This week: Emma Willmann, pajama comedy picking up steam, a socials hire ask, Zabar's Magic selling out in under 2 minutes, a Johnny Depp townhouse show, and a dream of the New York Stock Exchange.

Email david@comedyuo.com if you can help with any of it.`,

    comic: {
      label: "One comic to watch",
      title: "9/17 · Emma Willmann",
      image: "https://storage.googleapis.com/cuo-website/Emma%20Willmann%20.jpg",
      imageAlt: "Emma Willmann",
      caption: "A true HR nightmare. You take a course on pronouns, and then you run into Emma. These are not my words. They belong to Emma.",
      body: `Emma's consistency to kill on stage over the past 5 years has been amazing to see. The true jump for a comic is not when they sometimes do well, but when they can do it across 4 shows on the same night. The best comics are the ones who are the most self-aware - Emma is already established - but I believe she is well on her way to becoming a household name.`,
      ctaLabel: "Watch Emma",
      ctaHref: "https://www.youtube.com/watch?v=AYt-54fPKfU",
    },

    updates: {
      label: "New initiatives",
      title: "Pajama comedy is picking up steam",
      body: `Looking to hire someone for Socials. As a young Gen Z founder - I'm a bit handicapped with technology. Everything has been scrappy, but we are looking for someone with experience building out brands across multiple social platforms. We have a lot of exciting projects in the works - Characteristics we are looking for:

Self starter
I don't necessarily need more ideas. I need someone who can execute.
Please be organized.
Entrepreneurial spirit. I want this person to take ownership of this role and help build the voice of the brand. We want another founder, not an employee.`,
    },

    magic: {
      label: "Magic",
      title: "2,300 clicked the Zabar's link and crashed checkout",
      image: "https://storage.googleapis.com/cuo-website/Modi%20Zabar's.jpg",
      imageAlt: "Magic at Zabar's",
      body: `The hype for Magic is real folks. We sold out in under 2 minutes. We even had problems with Stripe due to the spike. I do want to apologize to the folks who had technical glitches when checking out. We may not get you into Zabar's Magic, but y'all will get early access to any upcoming show of your choice. This year we will do at least one Halloween-themed show, but I'm excited for 2027 to do a full October spooky season Magic takeover.`,
    },

    story: {
      label: "This actually happened",
      title: "Johnny Depp townhouse",
      image: "https://storage.googleapis.com/cuo-website/johnny%20depp%20show.jpg",
      imageAlt: "Johnny Depp townhouse show",
      body: `I won't give the exact address because now Zach Bryan lives there. I should ask him if he would be down to host us. About 2 years ago, a tech bro hit us up saying he had a townhouse in the West Village and wanted us to host a show there. It was about 20k a month in rent and was a 3-floor one-bedroom crib. Everyone had to wear slippers, and it was one of the coolest shows we've ever done. What's even cooler about this home is that Johnny Depp was one of the previous residents before the tech bro hit us up. I can't confirm this part of the story, but I believe Amber Heard lived with Johnny at the time he resided in the WV. We advertised this show by just saying 'next show is @Johnny Depp house,' and we sold 80 tickets immediately.`,
    },

    dream: {
      label: "Dreaming big... please help",
      title: "A show at the New York Stock Exchange",
      intro: `We've had over 10,000 people take a seat at one of our shows. I know a lot of y'all work in finance. How sick would it be to put on a show at the New York Stock Exchange? They close at 5 pm anyways. We wouldn't interfere with their work.`,
      body: `I'm not sure if Nasdaq is the same or separate, but any trading floor would be incredible for an after-hours event. Maybe we can take all the ticket proceeds and invest them into a stock that is selected by the audience. Who knows, but you get the gist.`,
      contribute: "Email david@comedyuo.com if you have a connection or anything to contribute!",
    },

    signoff: `Join our waitlist to come to our next big show!

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-09-10",
    publishedAt: "2026-09-10",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "9/10 · Edition 2",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Magic · This Actually Happened · Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

Welcome back. This week: Josh Ocean Thomas, a pajama comedy venue hunt, a spooky magic idea, a SoHo Ink story, and a real ask for connections.

If you or someone you know can help with any of the spaces below, email david@comedyuo.com.`,

    comic: {
      label: "One comic to watch",
      title: "9/10 · Josh Ocean Thomas",
      image: "https://storage.googleapis.com/cuo-website/JoshOcean%20Thomas%20.jpeg",
      imageAlt: "Josh Ocean Thomas",
      caption: "The funniest Tunisian in comedy.",
      body: `It's very weird for me to be highlighting comics that are younger than me. At only 25 years old and about a year living in NYC he's passed at all the clubs. He's got amazing energy and got a ton of momentum behind him. I think he has some SNL potential and excited to see where his career will be at in the next 5 years.`,
      ctaLabel: "Watch Josh",
      ctaHref: "https://www.instagram.com/reel/DZ22Y-YMxuu/?stkn=aGkzenV2YndiYmNq",
    },

    updates: {
      label: "New initiatives",
      title: "Pajama comedy in December",
      body: `Trying something new for the first time. We are going to build this plane out in the open. The goal is to find a new venue where we can produce a great themed comedy night. Pajama Comedy in December is what we settled on. I posted on our IG story asking for suggestions. We have gotten some great responses, but something that has stuck out has been that over 50 percent of the replies have recommended we pursue a mattress store for this show. These big chains can be difficult, but we are on it and appreciate the support. As we contact the mattress shops and any of the other venues, I will keep posting on the page to let folks know how the hunt is going. As mentioned last week, I'm trying to engage more with all the folks who have attended our shows and have continued to show a lot of love and support. Down below in the dream big section, I'm posting all of the suggestions we have received. If you or a loved one have a connection to any of the spaces, please reach out.`,
    },

    magic: {
      label: "Magic",
      title: "Spooky season magic tour?",
      image: "https://storage.googleapis.com/cuo-website/Magic%20Show%20Newsletter.jpg",
      imageAlt: "Magic UO show",
      body: `Every January we produce the small business food tour, which will be a stretch of 10 shows over the course of 2 weeks at some iconic locations. My dream is to create a similar tour in October around Halloween to lean into spooky season. What are y'all's thoughts on doing an event at a funeral home? I won't give the exact name, but we are in talks with a premier funeral home to do a magic show at their space.`,
    },

    story: {
      label: "This actually happened",
      title: "Shrooms before a show at SoHo Ink",
      image: "https://storage.googleapis.com/cuo-website/Soho%20INK.jpg",
      imageAlt: "SoHo Ink tattoo shop show",
      body: `January 5th, 2022 is the first time I ever did shrooms. We used to do a show at SoHo Ink, a tattoo shop in SoHo. The GM was a man named Jason who was a shaman on the side. He was tatted head to toe and he always invited me to ayahuasca retreats he did upstate. He was telling me about the positive effects of shrooms so for some crazy reason I did it right before a show. I typically get very anxious before an event and he told me shrooms would help. The only thing I didn't love is that he gave the shrooms to the host as well. While it did calm me down, the host bombed. I no longer allow psychedelic consumption before shows.`,
    },

    dream: {
      label: "Dreaming big... please help",
      title: "Pajama comedy night in December",
      intro: `As mentioned above, I want to do a big pajama comedy night in December. Maybe multiple nights - who knows! We've gotten a lot of great responses, and I'm listing the suggestions below. If you have any connections please don't be shy. Anyone who helps make this happen will get free tickets, new pajamas, and if we pull off a show at a mattress store - fuck it I may buy you a new mattress. Prices have to be reasonable.`,
      body: `If you have any connection to:
Brooke Linen Williamsburg
Build-a-Bear
Museum of Ice Cream
A/BC carpets
Mattress companies

Let us know - we'd loooove to be connected!`,
      contribute: "Email david@comedyuo.com if you have a connection or anything to contribute!",
    },

    signoff: `Thank you for supporting us! You guys mean the world to us.

Spread Love,
David`,
    footerImage: "",
    footerImageAlt: "",
    footerCaption: "",
  },
  {
    slug: "2026-08-31",
    publishedAt: "2026-08-31",
    title: "The Underground",

    tagline: "A weekly note from behind the scenes",
    edition: "8/31 · Edition 1",
    heroLabel: "All the shit that happens before the show and sometimes during",
    heroTitle: "The Underground",
    heroSummary:
      "One Comic To Watch · New Initiatives · Something Magical · Blast From The Past · We're Dreaming Big (Please Help)",

    noteLabel: "A note from David",
    noteBody: `Hey,

If you're reading this, it means you've shown us a ton of love and support over the last five years, whether you've been to a show or at least tried to make it to one a few times.

I'm really excited to start The Underground and use it as another way to stay connected with everyone who has supported us along the way.

I'm also very open to feedback. I'd love to know what you want to learn more about, what you don't give a sh*t about, and how we can make this something you actually look forward to.

You can also tell me if you think this is a horrible idea. I promise I won't be offended.`,

    comic: {
      label: "David's comic to watch",
      title: "8/31 · Jamie Wolf",
      image:
        "https://storage.googleapis.com/cuo-website/email-images/35be269b-1f33-4e44-8b09-5998756b1640.jpg",
      imageAlt: "Jamie Wolf",
      caption: "This here is Jamie Wolf assaulting an audience member in the front row.",
      body: `Jamie is one of those comics that you can just see is on the path to becoming great. Sometimes he needs a reminder, but the step-by-step progress he's made since working with him for the first time about 4 years ago is very noticeable.

I truly believe Jamie is the perfect example of someone who, if they continue to put in a little work each day, in another 2-3 years will be selling a couple thousand tickets a month.

The one major fault holding Jamie back is that his phone is always on DND. I despise that.

If you need a laugh, watch this joke.`,
      ctaLabel: "Watch David's pick",
      ctaHref: "https://youtube.com/shorts/Q_fqVTxtaDg",
    },

    updates: {
      label: "Meanwhile, at UO",
      title: "Getting y'all up to speed",
      body: `For the first 3 years of this business, there was not a part of the operation that Ethan and I did not touch. We walked into small businesses to secure venues, went to shows to book talent, physically set up the shows, went to parties to sell tickets. Now that we do 100+ events a year, we can't be doing that anymore.

The business today is made up of 4 full-time employees, including myself (David Levine), Jake Delforte aka Delfante, Chinatown Mike (Mike is white) but got an Airbnb in Chinatown when he first moved to NY, & Mariel.

Ethan, my co-founder, is part-time in the business as he pursues a career in dentistry. We have other characters such as OT, aka black dynamite, Beth, Will, Hannah, and Adam who are also a big part of UO. You'll have to stay tuned to learn more about their storylines.`,
    },

    magic: {
      label: "A little UO magic",
      title: "Something magical is happening!",
      image:
        "https://storage.googleapis.com/cuo-website/email-images/0d97016a-3e2a-496a-9c2c-04ccc457f5cc.jpg",
      imageAlt: "This week's magical moment",
      body: `I'm so excited for what I think Magic UO can become!
One of the projects we're currently working on is really trying to build on what we have done in Comedy and bring that to the Magic space. There are so many talented magicians in NYC, and I think it's starting to have its golden moment, similar to what we've seen with comedy over the last 3-5 years. It's another intimate activity that is unique and fun for our audience. Honestly, our biggest competitor right now is that anyone can just hire a magician to stroll around their party (which is great), but I think Magic can also have some structure similar to a comedy show where you create an incredible evening end-to-end. That's where MagicUO comes in.

Trying to figure out how to grow a following from scratch (again) is challenging because 5 years ago literally everything we did was accidental. 0 game plan - we just posted what we thought was fun. Timeout and influencer marketing played a huge role for ComedyUO. Those two forms of promotion have become a bit saturated.`,
    },

    story: {
      label: "This actually happened",
      title: "Originally known as the Capri Sun boys",
      image:
        "https://storage.googleapis.com/cuo-website/email-images/55c34e2e-3f67-4d8b-8d2f-f12a4d8b5cb9.jpg",
      imageAlt: "Behind the scenes at ComedyUO",
      body: `Fun fact: For the first year of doing shows, from April 2021 to April 2022, Ethan and I would spike thousands of Capri Suns and serve them at our events. We'd buy the pouches in bulk from Costco, pick up handles of tequila and vodka, and bring everything back to Ethan's apartment. Then, using syringes, we'd inject at least two shots' worth of alcohol into each Capri Sun before sealing the tiny hole with electrical tape.

And we did this for thousands of Capri Suns.

It was neither cost nor time efficient. But we didn't care. At that point, we were obsessed with making our shows as fun as possible, and this felt like one more little detail that could make the experience better. It was truly an act of love.

I still think about bringing back the Capri Suns and handing the operation off to an intern. I'm not entirely sure how legal that would be, but during that first year, people got *very* sauced. That included our bartenders and, occasionally, us.

Shoutout to Caleb, who once slept in my bathtub and helped get us kicked out of a residential building for being a little too rowdy.`,
    },

    dream: {
      label: "Dream big",
      title: "We love to dream big. Please help",
      intro: `We have big dreams over here. I struggle with sometimes openly sharing my ideas cause I'm scared of people hijacking them, but I feel if I put it in writing, hopefully that buys me some solidarity and time. As we know, in this world, it's who you know that matters. So each week, I will share my dream venues and some projects, and if by any chance you may be able to help us pull off one of our dreams, please reach out. We can move mountains together.`,
      body: `Here is the first dream I'm willing to share - THE ANNUAL MACY'S Day comedy event.
Is this not a home run idea? We have established ourselves for annual tentpole events in NY (Katz's, Empire State Building, Peter Luger's).

Why not Macy's? Macy's Herald Square location is world-renowned and already home to the iconic Thanksgiving Day parade and annual July 4th fireworks celebration. Being that NY is the Mecca for comedy, I think this is another great event to bring young NYers into the store.

Macy's is a 9-floor establishment that, if you walk the entire building end to end, it'll take you over an hour. Trust me, I've done it twice while scouting the venue. It is so big that we could activate each floor with a comedy show without there being any noise bleed between shows. Even crazier - Can you imagine floor 1 is a comedy show, floor 2 is a live comedy podcast, floor 3 is a magic show, and floor 4 is a live DJ? These are obviously hypotheticals. I'm just painting the picture of how we are pitching the idea as a win-win for both parties.

We know how to transform spaces after hours; we work with top talent, and the folks who come to our shows are city folks. That's what Macy's needs. Maybe I'm delusional, but we could push 1000 tickets for this quickly.

It's been hard to make headway - can you help?`,
      contribute: "Email david@comedyuo.com if you have anything to contribute!",
    },

    signoff: `With that, I'll see you next week!

Spread Love,
David`,
    footerImage:
      "https://storage.googleapis.com/cuo-website/email-images/b1941a9f-a7ba-4b06-8031-50dd1d296584.jpg",
    footerImageAlt: "Flip cup after a ComedyUO show",
    footerCaption:
      "A lil flip cup action after a recent comedy show. Comedy and magic in New York City's most unexpected places.",
  },
];

/** Newest first: current issue, then archives. */
export const ALL_NEWSLETTER_ISSUES: NewsletterIssue[] = [
  CURRENT_NEWSLETTER,
  ...NEWSLETTER_ARCHIVE,
];

export function newsletterHref(issue: NewsletterIssue): string {
  if (issue.slug === CURRENT_NEWSLETTER.slug) return "/newsletter";
  return `/newsletter/${issue.slug}`;
}

export function getNewsletterIssue(slug: string | undefined): NewsletterIssue | undefined {
  if (!slug) return CURRENT_NEWSLETTER;
  return ALL_NEWSLETTER_ISSUES.find((issue) => issue.slug === slug);
}

export function getAdjacentNewsletters(slug: string): {
  older?: NewsletterIssue;
  newer?: NewsletterIssue;
} {
  const index = ALL_NEWSLETTER_ISSUES.findIndex((issue) => issue.slug === slug);
  if (index < 0) return {};
  return {
    newer: index > 0 ? ALL_NEWSLETTER_ISSUES[index - 1] : undefined,
    older: index < ALL_NEWSLETTER_ISSUES.length - 1 ? ALL_NEWSLETTER_ISSUES[index + 1] : undefined,
  };
}
